import { getDealRevenueCents } from '../../../../shared/utils/dealRevenue';
import { splitInstallments, commitInstallmentEdit, formatCentsForInput, isValidMoneyDraft } from '../../../../shared/utils/installments';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  applyClientSummaryToDealForm,
  getDealKindMeta,
  normalizeDealForForm,
  validateDealForm,
} from '@/modules/deals/shared/utils/dealAdapters';
import {
  createDealCommentResult,
  createDealCommentReplyResult,
  listDealCommentsResult,
} from '@/modules/deals/services';
import { toast } from '../../../../shared/hooks/use-toast';

export function useDealDetailState({
  deal,
  dealType,
  clients = [],
  onClose,
  onSave,
  onMove,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [installmentDrafts, setInstallmentDrafts] = useState({});
  const [editedDeal, setEditedDeal] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [commentsState, setCommentsState] = useState({
    status: 'idle',
    message: '',
  });
  const [expandedComments, setExpandedComments] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySubmittingTo, setReplySubmittingTo] = useState(null);
  const replySubmittingRef = useRef(null);
  const [newDealType, setNewDealType] = useState(dealType);
  const [isSaving, setIsSaving] = useState(false);
  const savePromiseRef = useRef(null);
  const meta = useMemo(() => getDealKindMeta(dealType), [dealType]);

  useEffect(() => {
    if (!deal) return;

    setEditedDeal(normalizeDealForForm(deal, dealType));
    setInstallmentDrafts({});
    setNewDealType(dealType);
    setIsEditing(false);
    setNewComment('');
    setCommentsState({
      status: deal.id ? 'loading' : 'idle',
      message: '',
    });
    setReplyText('');
    setReplyingTo(null);
    setReplySubmittingTo(null);
    replySubmittingRef.current = null;
    setExpandedComments({});
    if (!deal.id) {
      return;
    }

    let active = true;
    async function loadComments() {
      const result = await listDealCommentsResult(dealType, deal.id);
      if (!active) return;

      if (!result.ok) {
        setCommentsState({
          status: 'error',
          message: result.message || 'Commenti non disponibili.',
        });
        return;
      }

      setEditedDeal((prev) => ({
        ...prev,
        commenti: result.data,
      }));
      setCommentsState({
        status: 'success',
        message: '',
      });
    }

    loadComments();

    return () => {
      active = false;
    };
  }, [deal, dealType]);

  function updateField(field, value) {
    if (field === meta.amountField || field === 'probability') setInstallmentDrafts({});
    setEditedDeal((prev) => {
      const next = { ...prev, [field]: value };
      if (field !== meta.amountField && field !== 'probability') return next;
      return { ...next, dilazionamento: { ...prev.dilazionamento,
        importiRate: splitInstallments(getDealRevenueCents(next), prev.dilazionamento.scadenze.length || 1), amountError: '',
      } };
    });
  }

  function updateNestedField(group, field, value) {
    setEditedDeal((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [field]: value,
      },
    }));
  }

  function handleClientRelink(clientId) {
    const client = clients.find((item) => String(item.id) === String(clientId));
    if (!client) return;

    setEditedDeal((prev) => applyClientSummaryToDealForm(prev, client));
  }

  function addScadenza() {
    setInstallmentDrafts({});
    setEditedDeal((prev) => {
      const scadenze = prev?.dilazionamento?.scadenze || [];

      return {
        ...prev,
        dilazionamento: {
          ...prev.dilazionamento,
          scadenze: [...scadenze, ''],
          rate: scadenze.length + 1,
          importiRate: splitInstallments(getDealRevenueCents(prev), scadenze.length + 1),
          amountError: '',
        },
      };
    });
  }

  function updateScadenza(index, value) {
    setEditedDeal((prev) => {
      const scadenze = [...(prev?.dilazionamento?.scadenze || [])];
      scadenze[index] = value;

      return {
        ...prev,
        dilazionamento: {
          ...prev.dilazionamento,
          scadenze,
          rate: scadenze.length,
        },
      };
    });
  }

  function removeScadenza(index) {
    setInstallmentDrafts({});
    setEditedDeal((prev) => {
      const dates = (prev?.dilazionamento?.scadenze || []).filter((_, itemIndex) => itemIndex !== index);
      const scadenze = dates.length ? dates : [''];
      return { ...prev, dilazionamento: { ...prev.dilazionamento, scadenze, rate: scadenze.length,
        importiRate: splitInstallments(getDealRevenueCents(prev), scadenze.length), amountError: '',
      } };
    });
  }

  function beginInstallmentAmountEdit(index) {
    setInstallmentDrafts((prev) => ({ ...prev, [index]: prev[index] ?? formatCentsForInput(editedDeal.dilazionamento.importiRate[index]) }));
  }

  function updateInstallmentAmount(index, value) {
    if (isValidMoneyDraft(value)) setInstallmentDrafts((prev) => ({ ...prev, [index]: value }));
  }

  function commitInstallmentAmount(index) {
    if (!Object.prototype.hasOwnProperty.call(installmentDrafts, index)) return;
    const result = commitInstallmentEdit(editedDeal.dilazionamento.importiRate, index, installmentDrafts[index], getDealRevenueCents(editedDeal));
    if (!result.error) setInstallmentDrafts({});
    setEditedDeal((prev) => ({ ...prev, dilazionamento: { ...prev.dilazionamento,
      importiRate: result.amounts.length ? result.amounts : prev.dilazionamento.importiRate, amountError: result.error,
    } }));
  }

  function addTeamMember() {
    setEditedDeal((prev) => ({
      ...prev,
      teamCoinvolto: [...(prev?.teamCoinvolto || []), ''],
    }));
  }

  function updateTeamMember(index, value) {
    setEditedDeal((prev) => {
      const teamCoinvolto = [...(prev?.teamCoinvolto || [])];
      teamCoinvolto[index] = value;

      return {
        ...prev,
        teamCoinvolto,
      };
    });
  }

  function removeTeamMember(index) {
    setEditedDeal((prev) => ({
      ...prev,
      teamCoinvolto: (prev?.teamCoinvolto || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  }

  async function handleAddComment(payload = null) {
    const body = String(payload?.body ?? newComment).trim();
    if (!body) return { ok: false };

    const result = await createDealCommentResult(dealType, editedDeal?.id, body);
    if (!result.ok) {
      setCommentsState({
        status: 'error',
        message: result.message || 'Commento non salvato.',
      });
      return;
    }

    setEditedDeal((prev) => ({
      ...prev,
      commenti: [...(prev?.commenti || []), result.data],
    }));
    setNewComment('');
    setCommentsState({
      status: 'success',
      message: '',
    });
    return result;
  }

  async function handleAddReply(commentId, payload = null) {
    const body = String(payload?.body ?? replyText).trim();
    if (!body) return { ok: false };
    if (replySubmittingRef.current === commentId) return;

    replySubmittingRef.current = commentId;
    setReplySubmittingTo(commentId);
    let result;
    try {
      result = await createDealCommentReplyResult(dealType, editedDeal?.id, commentId, body);
    } finally {
      replySubmittingRef.current = null;
      setReplySubmittingTo(null);
    }

    if (!result.ok) {
      setCommentsState({
        status: 'error',
        message: result.message || 'Risposta non salvata.',
      });
      return;
    }

    setEditedDeal((prev) => ({
      ...prev,
      commenti: (prev?.commenti || []).map((comment) =>
        comment.id === commentId
          ? {
              ...comment,
              risposte: [...(comment.risposte || []), result.data],
              replies: [...(comment.replies || comment.risposte || []), result.data],
            }
          : comment
      ),
    }));
    setReplyText('');
    setReplyingTo(null);
    setCommentsState({
      status: 'success',
      message: '',
    });
    return result;
  }

  function toggleCommentExpand(commentId) {
    setExpandedComments((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  }

  function handleSave() {
    if (savePromiseRef.current) {
      return savePromiseRef.current;
    }

    const validationError = validateDealForm(editedDeal);
    if (validationError) {
      toast({ title: 'Affare non valido', description: validationError, variant: 'destructive' });
      return Promise.resolve({
        ok: false,
        error: 'DEAL_VALIDATION_ERROR',
        message: validationError,
      });
    }

    setIsSaving(true);
    const request = (async () => {
      try {
        if (newDealType !== dealType && onMove) {
          const moveResult = await onMove(editedDeal, newDealType);
          if (moveResult?.ok === false) {
            return moveResult;
          }

          setIsEditing(false);
          onClose();
          return moveResult;
        }

        const saveResult = await onSave?.(editedDeal);
        if (saveResult?.ok === false) {
          return saveResult;
        }

        setIsEditing(false);
        return saveResult;
      } finally {
        savePromiseRef.current = null;
        setIsSaving(false);
      }
    })();
    savePromiseRef.current = request;
    return request;
  }

  function handleCancel() {
    if (savePromiseRef.current) return;
    setEditedDeal((current) => ({
      ...normalizeDealForForm(deal, dealType),
      commenti: current?.commenti || [],
    }));
    setInstallmentDrafts({});
    setNewDealType(dealType);
    setIsEditing(false);
  }

  return {
    meta,
    isEditing,
    isSaving,
    setIsEditing,
    editedDeal,
    newComment,
    setNewComment,
    commentsState,
    expandedComments,
    replyingTo,
    setReplyingTo,
    replyText,
    setReplyText,
    replySubmittingTo,
    newDealType,
    setNewDealType,
    updateField,
    updateNestedField,
    handleClientRelink,
    addScadenza,
    updateScadenza,
    installmentDrafts, beginInstallmentAmountEdit, updateInstallmentAmount, commitInstallmentAmount,
    removeScadenza,
    addTeamMember,
    updateTeamMember,
    removeTeamMember,
    handleAddComment,
    handleAddReply,
    toggleCommentExpand,
    handleSave,
    handleCancel,
  };
}
